import { useCallback, useReducer, useRef } from 'react'
import type { DocumentPatch } from '../core/model'
import {
  agentReducer,
  initialAgentState,
  isAgentActive,
} from './machine'
import { matchScenario } from './scenarios'

export interface AgentControls {
  state: typeof initialAgentState
  run: (prompt: string) => Promise<void>
  pause: () => void
  resume: () => void
  cancel: () => void
  retry: () => void
  reset: () => void
}

function abortError(): DOMException {
  return new DOMException('Agent run cancelled', 'AbortError')
}

function sleep(duration: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError())
      return
    }
    const timer = window.setTimeout(resolve, duration)
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer)
        reject(abortError())
      },
      { once: true },
    )
  })
}

export function chunkStreamText(value: string, size = 11): string[] {
  if (size < 1) throw new Error('Chunk size must be positive.')
  const chunks: string[] = []
  for (let index = 0; index < value.length; index += size) {
    chunks.push(value.slice(index, index + size))
  }
  return chunks
}

export function useAgentRun(
  applyPatch: (patch: DocumentPatch, label: string) => void,
  createCheckpoint: (message: string) => void,
): AgentControls {
  const [state, dispatch] = useReducer(agentReducer, initialAgentState)
  const controllerRef = useRef<AbortController | null>(null)
  const pausedRef = useRef(false)
  const lastPromptRef = useRef('')

  const waitForControl = useCallback(async (signal: AbortSignal) => {
    while (pausedRef.current) await sleep(50, signal)
    if (signal.aborted) throw abortError()
  }, [])

  const run = useCallback(
    async (rawPrompt: string) => {
      const prompt = rawPrompt.trim()
      if (!prompt) return

      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      pausedRef.current = false
      lastPromptRef.current = prompt
      const scenario = matchScenario(prompt)
      dispatch({
        type: 'START',
        prompt,
        steps: scenario.steps.map(({ id, label }) => ({ id, label })),
        now: performance.now(),
      })

      try {
        await sleep(170, controller.signal)
        for (const step of scenario.steps) {
          await waitForControl(controller.signal)
          dispatch({ type: 'STEP_START', stepId: step.id })

          const chunks = chunkStreamText(step.narrative)
          for (const chunk of chunks) {
            await waitForControl(controller.signal)
            await sleep(22 + Math.random() * 24, controller.signal)
            await waitForControl(controller.signal)
            dispatch({ type: 'TOKEN', value: chunk, now: performance.now() })
          }

          if (step.patch) {
            dispatch({ type: 'APPLY' })
            await sleep(140, controller.signal)
            await waitForControl(controller.signal)
            applyPatch(step.patch, step.label)
          }

          dispatch({ type: 'STEP_DONE', stepId: step.id })
          dispatch({ type: 'TOKEN', value: '\n\n', now: performance.now() })
        }

        dispatch({ type: 'VERIFY' })
        await sleep(260, controller.signal)
        await waitForControl(controller.signal)
        createCheckpoint(`Agent · ${scenario.label}`)
        dispatch({ type: 'COMPLETE' })
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          dispatch({ type: 'CANCEL' })
          return
        }
        dispatch({
          type: 'FAIL',
          error: error instanceof Error ? error.message : 'Agent run failed.',
        })
      }
    },
    [applyPatch, createCheckpoint, waitForControl],
  )

  const pause = useCallback(() => {
    pausedRef.current = true
    dispatch({ type: 'PAUSE' })
  }, [])

  const resume = useCallback(() => {
    pausedRef.current = false
    dispatch({ type: 'RESUME' })
  }, [])

  const cancel = useCallback(() => {
    controllerRef.current?.abort()
  }, [])

  const retry = useCallback(() => {
    if (lastPromptRef.current) void run(lastPromptRef.current)
  }, [run])

  const reset = useCallback(() => {
    if (isAgentActive(state.phase)) controllerRef.current?.abort()
    dispatch({ type: 'RESET' })
  }, [state.phase])

  return { state, run, pause, resume, cancel, retry, reset }
}
